import "./valet_report.page.style.scss";
import { FC } from "react";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import useValetReportViewModel from "./valet_report.page.view_model";
import LayoutComponent from "../../../layouts/layoutComponent/LayoutComponent";
import ButtonComponent from "../../../components/button/ButtonComponent";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent";
import NotResultsComponent from "../../../components/noResults/NoResultsComponent";
import AutoCompleteWithCheckComponent from "../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";
import Validators from "../../../utils/Validators";
import { ErrorMessage } from "@hookform/error-message";
import { ShiftStatus } from "../../../../domain/entities/ShiftEntity";

const ValetReportPage: FC<{}> = () => {
    const {
        formInfo,
        reportData,
        loading,
        i18n,
        _handleSearch,
        _loadReport,
        _downloadReport,
        _onSearchValets,
        _handleChange,
        handleChangeValet,
        getMinDate,
        getMaxDate,
        errors,
        register,
        handleSubmit,
    } = useValetReportViewModel();

    const formatDate = (date: Date): string => {
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatHour = (date: Date): string => {
        return date.toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    return (
        <div className="valet_report_page">
            <LayoutComponent title={i18n("Reporte de Valet")}>
                <div className="container py-5">
                    <form onSubmit={handleSubmit(_handleSearch)}>
                        <div className="row">
                            {/* Selector de Valet */}
                            <div className="col-md-6">
                                <AutoCompleteWithCheckComponent
                                    keyName={KeyWordLocalization.ValetReportPageValet}
                                    label={i18n("Valet")}
                                    formInfo={formInfo}
                                    multiple={false}
                                    onSearch={_onSearchValets}
                                    onChange={handleChangeValet}
                                />
                            </div>

                            {/* Fecha de Inicio */}
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label className="form-label">
                                        {i18n("Fecha de Inicio")}
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        min={getMinDate()}
                                        max={getMaxDate()}
                                        {...register(KeyWordLocalization.ValetReportPageStartDate, Validators({ required: true, onChange: _handleChange }))}

                                    />
                                    <ErrorMessage
                                        as="aside"
                                        errors={errors}
                                        name={KeyWordLocalization.ValetReportPageStartDate}
                                    />
                                </div>
                            </div>

                            {/* Fecha de Fin */}
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label className="form-label">
                                        {i18n("Fecha de Fin")}
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        min={getMinDate()}
                                        max={getMaxDate()}
                                        {...register(KeyWordLocalization.ValetReportPageEndDate, Validators({ required: true, onChange: _handleChange }))}
                                    />
                                    <ErrorMessage
                                        as="aside"
                                        errors={errors}
                                        name={KeyWordLocalization.ValetReportPageEndDate}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Botón de descarga */}
                        <div className="row mt-3">
                            <div className="col-12 d-flex justify-content-end">
                                <ButtonComponent
                                    className="d-flex justify-content-center"
                                    icon={<span className="material-symbols-outlined">download</span>}
                                    text={i18n("Descargar Excel")}
                                    design="primaryOutline"
                                    type="button"
                                    isLoading={loading}
                                    onClick={_downloadReport}
                                />
                            </div>
                        </div>
                    </form>

                    {/* Contenido del reporte */}
                    <div className="report-content mt-5">
                        {loading && (
                            <div className="text-center">
                                <LoadingComponent />
                            </div>
                        )}

                        {!loading && !reportData && (
                            <NotResultsComponent
                                message={i18n("Selecciona un valet y fechas para ver el reporte")}
                                subtitle={i18n("Completa todos los campos arriba para generar el reporte")}
                                icon="person_search"
                                variant="info"
                            />
                        )}

                        {!loading && reportData && (
                            <div className="report-data">
                                {/* Cards de estadísticas directas */}
                                <div className="row mb-4">
                                    <div className="col-md-6 mb-3">
                                        <div className="chart_card">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title m-0">
                                                        <span className="material-symbols-outlined me-2">schedule</span>
                                                        Horas Trabajadas
                                                    </h6>
                                                </div>
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{reportData.workedHours || 0}h</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <div className="chart_card">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title m-0">
                                                        <span className="material-symbols-outlined me-2">schedule</span>
                                                        Total de Turnos
                                                    </h6>
                                                </div>
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{(reportData.assistedShifts + reportData.missedShifts) || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="chart_card">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title m-0">
                                                        <span className="material-symbols-outlined me-2">check_circle</span>
                                                        Turnos Asistidos
                                                    </h6>
                                                </div>
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{reportData.assistedShifts || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="chart_card">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title m-0">
                                                        <span className="material-symbols-outlined me-2">info</span>
                                                        Turnos No Confirmados
                                                    </h6>
                                                </div>
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{reportData.pendingShifts || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="chart_card">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="card-title m-0">
                                                        <span className="material-symbols-outlined me-2">cancel</span>
                                                        Turnos Rechazados
                                                    </h6>
                                                </div>
                                                <div className="card-body text-center">
                                                    <h3 className="mb-0">{reportData.missedShifts || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabla de turnos directa */}
                                {reportData.shifts && reportData.shifts.length > 0 && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="table-responsive">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>{i18n("Fecha")}</th>
                                                            <th>{i18n("Establecimiento")}</th>
                                                            <th>{i18n("Horario")}</th>
                                                            <th>{i18n("Estado")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reportData.shifts.map((shift, index) => (
                                                            <tr key={index}>
                                                                <td>{formatDate(shift.startDate)}</td>
                                                                <td>{shift.stablishment.name}</td>
                                                                <td>{shift.checkedInAt || shift.startDate ? formatHour(shift.checkedInAt ?? shift.startDate) : shift.schedule.openTime} -
                                                                    {shift.checkedOutAt || shift.endDate ? formatHour(shift.checkedOutAt ?? shift.endDate) : shift.schedule.closeTime}</td>
                                                                <td>
                                                                    <span className={`badge ${shift.status === ShiftStatus.FINISHED ? 'bg-success' : 'bg-warning'}`}>
                                                                        {shift.status === ShiftStatus.FINISHED ? i18n("Completado") : i18n("Pendiente")}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </LayoutComponent>
        </div>
    );
};

export default ValetReportPage;